import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CodeBlock,
  GuideBulletList,
  GuideCodeTable,
  GuideHeading,
  GuideModal,
  GuideNote,
  GuideSection,
  GuideSteps,
  GuideText,
} from 'modules/settings/components/DataExport/GuideContent';
import { Code, GuideBody } from 'modules/settings/components/DataExport/styled';

/** Never a real token: the samples read the token from an environment variable. */
export const TOKEN_PLACEHOLDER = '$LATANA_EXPORT_TOKEN';

const STEPS = ['listStudies', 'readManifest', 'downloadCsv', 'checkAndLoad'] as const;
const RESPONSE_CODES = ['401', '404', '409', '429', '503'] as const;

/** A scheduled job that downloads every study's latest CSV; `BASE` defaults to this organisation's download API. */
export const pythonJobExample = (url: string): string => `import hashlib, json, os, urllib.error, urllib.request

BASE = os.environ.get("LATANA_EXPORT_URL", ${JSON.stringify(url)})
TOKEN = os.environ["LATANA_EXPORT_TOKEN"]


def get(url):
    request = urllib.request.Request(url, headers={"Authorization": f"Bearer {TOKEN}"})
    return urllib.request.urlopen(request, timeout=300)


def download_study(public_id, last_seen_sequence=None):
    for _ in range(3):  # re-read the manifest if a newer export replaces it mid-download
        manifest = json.load(get(f"{BASE}/v1/studies/{public_id}/manifest"))
        if manifest["export_sequence"] == last_seen_sequence:
            return None  # nothing new
        try:
            body = get(manifest["csv"]["url"]).read()
        except urllib.error.HTTPError as error:
            if error.code == 409:
                continue
            raise
        if hashlib.sha256(body).hexdigest() != manifest["csv"]["sha256"]:
            raise RuntimeError("checksum mismatch, retry later")
        return manifest, body
    raise RuntimeError("the study kept changing; retry later")


for study in json.load(get(f"{BASE}/v1/studies"))["studies"]:
    result = download_study(study["public_id"])
    if result:
        manifest, csv_bytes = result
        with open(f"{study['public_id']}.csv", "wb") as handle:
            handle.write(csv_bytes)
        with open(f"{study['public_id']}-manifest.json", "w") as handle:
            json.dump(manifest, handle, indent=2)
`;

const endpointFor = (step: typeof STEPS[number], url: string): string | null =>
  ({
    listStudies: `GET ${url}/v1/studies`,
    readManifest: `GET ${url}/v1/studies/{public_id}/manifest`,
    downloadCsv: `GET ${url}/v1/studies/{public_id}/data.csv?export_sequence={n}`,
    checkAndLoad: null,
  }[step]);

const DownloadApiGuide: React.FC<{ downloadApiUrl: string }> = ({ downloadApiUrl: url }) => {
  const { t } = useTranslation();
  const authHeader = `-H "Authorization: Bearer ${TOKEN_PLACEHOLDER}"`;
  const examples = [
    `curl ${authHeader} ${url}/v1/studies`,
    `curl ${authHeader} ${url}/v1/studies/<public_id>/manifest`,
    `curl -f ${authHeader} -o manifest.json "${url}/v1/studies/<public_id>/manifest.json?export_sequence=<n>"`,
    `curl -f ${authHeader} -o data.csv "${url}/v1/studies/<public_id>/data.csv?export_sequence=<n>"`,
  ];

  return (
    <>
      <GuideModal id='data-export-download-guide' title={t('dataExport.tokens.guide.title')}>
        <GuideBody data-testid='data-export-download-guide'>
          <GuideNote>
            <GuideText>{t('dataExport.tokens.guide.auth')}</GuideText>
          </GuideNote>
          <GuideSteps>
            {STEPS.map(step => {
              const endpoint = endpointFor(step, url);
              return (
                <li key={step}>
                  <GuideText>{t(`dataExport.tokens.guide.steps.${step}.title`)}</GuideText>
                  {endpoint && <Code>{endpoint}</Code>}
                  <GuideText>{t(`dataExport.tokens.guide.steps.${step}.description`)}</GuideText>
                </li>
              );
            })}
          </GuideSteps>
          <GuideSection>
            <GuideHeading>{t('dataExport.tokens.guide.examplesTitle')}</GuideHeading>
            {examples.map(example => (
              <CodeBlock key={example} code={example} language='bash' wrap data-testid='data-export-code-example' />
            ))}
          </GuideSection>
          <GuideSection>
            <GuideHeading>{t('dataExport.tokens.guide.pythonTitle')}</GuideHeading>
            <GuideText>{t('dataExport.tokens.guide.python')}</GuideText>
            <CodeBlock code={pythonJobExample(url)} language='python' data-testid='data-export-python-example' />
          </GuideSection>
          <GuideSection>
            <GuideHeading>{t('dataExport.tokens.guide.responsesTitle')}</GuideHeading>
            <GuideCodeTable rows={RESPONSE_CODES.map(code => ({ code, text: t(`dataExport.tokens.guide.responses.${code}`) }))} />
          </GuideSection>
          <GuideSection>
            <GuideHeading>{t('dataExport.tokens.guide.rotationTitle')}</GuideHeading>
            <GuideBulletList
              items={[
                t('dataExport.tokens.guide.rotation'),
                t('dataExport.tokens.guide.retention'),
                t('dataExport.tokens.guide.eventFeed'),
              ]}
            />
          </GuideSection>
        </GuideBody>
      </GuideModal>
    </>
  );
};

export default React.memo(DownloadApiGuide);
